import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitch from "components/common/LanguageSwitch";
import ProducerImport from "views/producers/ProducerImport";
import { mediaUrl } from "utils/mediaUrl";
import {
  AcceptTermsAPI, GetPortalAPI, ProposeNewWineAPI, ProposeWineChangeAPI, UploadPortalImageAPI,
} from "network/producers";

// Portale pubblico "Wine Data" per le cantine: raggiungibile solo dal link personale, senza login.
const GOLD = "#b8932f";
const STYLE = `
.pp{min-height:100vh;background:#f5f2ea;color:#222;font-family:inherit;padding-bottom:40px}
.pp *{box-sizing:border-box}
.pp-top{background:#14120f;color:#fff;padding:16px 18px;display:flex;justify-content:space-between;align-items:center;gap:10px}
.pp-top .lang-switch{font-size:.8rem}
.pp-top b{color:#e8c766;letter-spacing:.05em}
.pp-wrap{max-width:860px;margin:0 auto;padding:14px}
.pp-card{background:#fff;border:1px solid #e5dfd0;border-radius:14px;padding:14px 16px;margin-bottom:12px}
.pp-row{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap}
.pp-btn{min-height:44px;border:0;border-radius:10px;padding:0 18px;font-weight:600;background:${GOLD};color:#fff;cursor:pointer}
.pp-btn.sec{background:#fff;color:${GOLD};border:1px solid ${GOLD}}
.pp-btn:disabled{opacity:.5;cursor:default}
.pp-in,.pp-ta{width:100%;border:1px solid #cfc7b2;border-radius:8px;padding:8px 10px;font-size:.95rem;font-family:inherit}
.pp-ta{min-height:80px}
.pp-lab{display:block;font-size:.8rem;color:#666;margin:10px 0 3px}
.pp-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:0 14px}
.pp-badge{font-size:.75rem;border-radius:8px;padding:2px 8px;background:#f1e7c4;color:#7d6209}
.pp-img{width:100%;max-height:160px;object-fit:contain;border:1px solid #eee8d8;border-radius:8px;background:#fafafa}
.pp-sub{color:#777;font-size:.88rem}
.pp-msg{padding:10px 12px;border-radius:10px;margin-bottom:12px;background:#e8f3e6;color:#285c24}
.pp-msg.err{background:#fbe9e7;color:#8c2a1f}
`;

// Campi della scheda: qui solo gli identificativi (nomi dei dati), le etichette sono in producers:portal.fields.*
const TEXT = ["denomination", "grapes", "region", "country", "colour", "ean"];
const NUMBERS = ["gradation", "format", "energy", "sugar"];
const LONG = ["description", "appearance", "aroma", "taste", "pairing", "vinification", "terroir"];
const FLAGS = ["bio", "natural", "biodynamic", "have_sulphite"];
const SLOTS = ["front_bottle_image", "back_bottle_image", "front_label_image", "back_label_image"];

const WineForm = ({ wine, isNew, onSave, onCancel, busy }) => {
  const { t } = useTranslation("producers");
  const [f, setF] = useState(() => ({ ...wine }));
  const [files, setFiles] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const changed = () => {
    const out = {};
    [...TEXT, ...NUMBERS, ...LONG].forEach((k) => {
      const a = f[k] ?? "", b = wine[k] ?? "";
      if (String(a) !== String(b)) out[k] = a;
    });
    FLAGS.forEach((k) => { if ((f[k] ?? null) !== (wine[k] ?? null) && typeof f[k] === "boolean") out[k] = f[k]; });
    if (isNew) { out.name = f.name || ""; if (f.vintage) out.vintage = f.vintage; }
    return out;
  };
  const input = (k, Tag = "input", extra = {}) => (
    <div key={k}>
      <label className="pp-lab">{t(`portal.fields.${k}`)}</label>
      <Tag className={Tag === "input" ? "pp-in" : "pp-ta"} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} {...extra} />
    </div>
  );
  return (
    <div>
      {isNew && (
        <div className="pp-grid">
          {input("name")}
          {input("vintage", "input", { inputMode: "numeric" })}
        </div>
      )}
      <div className="pp-grid">
        {TEXT.map((x) => input(x))}
        {NUMBERS.map((x) => input(x, "input", { inputMode: "decimal" }))}
      </div>
      {LONG.map((x) => input(x, "textarea"))}
      <div className="pp-row" style={{ justifyContent: "flex-start", gap: 16, margin: "12px 0" }}>
        {FLAGS.map((k) => (
          <label key={k}>
            <input type="checkbox" checked={f[k] === true} onChange={(e) => set(k, e.target.checked)} /> {t(`portal.fields.${k}`)}
          </label>
        ))}
      </div>
      <div className="pp-grid">
        {SLOTS.map((slot) => (
          <div key={slot}>
            <label className="pp-lab">{t(`portal.slots.${slot}`)}</label>
            {(files[slot] || (!isNew && wine[slot])) && (
              <img className="pp-img" alt={t(`portal.slots.${slot}`)} src={files[slot] ? URL.createObjectURL(files[slot]) : mediaUrl(wine[slot])} />
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFiles((p) => ({ ...p, [slot]: e.target.files[0] }))} />
          </div>
        ))}
      </div>
      <div className="pp-row" style={{ marginTop: 16 }}>
        <button className="pp-btn sec" onClick={onCancel} disabled={busy}>{t("portal.cancel")}</button>
        <button className="pp-btn" disabled={busy || (isNew && !f.name)} onClick={() => onSave(changed(), files)}>
          {busy ? t("portal.sending") : t("portal.submit")}
        </button>
      </div>
    </div>
  );
};

export default function ProducerPortal() {
  const { t } = useTranslation("producers");
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null); // uuid | "new"
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await GetPortalAPI(token);
      setData(res.data);
    } catch (e) {
      setError(e?.message || t("portal.invalidLink"));
    }
  }, [token, t]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data?.wines || []).filter((w) => !t || `${w.name} ${w.vintage || ""}`.toLowerCase().includes(t));
  }, [data, q]);

  const accept = async () => {
    setBusy(true);
    try { await AcceptTermsAPI(token); await load(); } catch (e) { setNotice({ err: true, text: e?.message }); }
    setBusy(false);
  };

  const save = async (wine, fields, files) => {
    setBusy(true);
    setNotice(null);
    try {
      const hasFiles = Object.values(files).some(Boolean);
      const res = wine
        ? await ProposeWineChangeAPI(token, wine.uuid, { ...fields, with_images: hasFiles })
        : await ProposeNewWineAPI(token, fields);
      const sid = res.data.submission;
      for (const [slot, file] of Object.entries(files)) {
        if (file) await UploadPortalImageAPI(token, sid, slot, file);
      }
      setNotice({ text: t("portal.thanks") });
      setEditing(null);
      await load();
      window.scrollTo(0, 0);
    } catch (e) {
      setNotice({ err: true, text: e?.message || t("portal.sendFailed") });
    }
    setBusy(false);
  };

  return (
    <div className="pp">
      <style>{STYLE}</style>
      <div className="pp-top"><span><b>WINE DATA</b> · {t("portal.title")}</span><LanguageSwitch /></div>
      <div className="pp-wrap">
        {error && <div className="pp-card"><h3>{t("portal.invalidLinkTitle")}</h3><p>{error}</p></div>}
        {!error && !data && <p>{t("portal.loading")}</p>}
        {data && (
          <>
            <h2 style={{ marginTop: 8 }}>{data.producer}</h2>
            <p className="pp-sub">
              {t("portal.intro")}
            </p>
            {notice && <div className={`pp-msg ${notice.err ? "err" : ""}`}>{notice.text}</div>}

            {!data.terms_accepted ? (
              <div className="pp-card">
                <h4>{t("portal.terms")}</h4>
                <p>{data.terms_text}</p>
                <label><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> {t("portal.accept")}</label>
                <div style={{ marginTop: 12 }}><button className="pp-btn" disabled={!agree || busy} onClick={accept}>{t("portal.continue")}</button></div>
              </div>
            ) : editing === "new" ? (
              <div className="pp-card">
                <h4>{t("portal.newWine")}</h4>
                <WineForm wine={{}} isNew busy={busy} onCancel={() => setEditing(null)} onSave={(fields, files) => save(null, fields, files)} />
              </div>
            ) : (
              <>
                <ProducerImport
                  token={token}
                  onDone={async (r) => {
                    setNotice({ text: t("portal.importSent", { created: r.created, updated: r.updated }) });
                    await load();
                    window.scrollTo(0, 0);
                  }}
                />
                <div className="pp-row" style={{ marginBottom: 12 }}>
                  <input className="pp-in" style={{ maxWidth: 320 }} placeholder={t("portal.search")} value={q} onChange={(e) => setQ(e.target.value)} />
                  <button className="pp-btn" onClick={() => { setNotice(null); setEditing("new"); }}>{t("portal.addWine")}</button>
                </div>
                {data.new_wines.map((n) => (
                  <div className="pp-card" key={n.uuid}>
                    <div className="pp-row"><b>{n.name}</b><span className="pp-badge">{t("portal.newPending")}</span></div>
                  </div>
                ))}
                {shown.map((w) => (
                  <div className="pp-card" key={w.uuid}>
                    {editing === w.uuid ? (
                      <>
                        <h4>{w.name} {w.vintage || ""}</h4>
                        <WineForm wine={w} busy={busy} onCancel={() => setEditing(null)} onSave={(fields, files) => save(w, fields, files)} />
                      </>
                    ) : (
                      <div className="pp-row">
                        <div>
                          <b>{w.name}</b> {w.vintage || ""}
                          <div className="pp-sub">{[w.denomination, w.grapes, w.region].filter(Boolean).join(" · ") || t("portal.incomplete")}</div>
                        </div>
                        <div>
                          {w.pending && <span className="pp-badge" style={{ marginRight: 8 }}>{t("portal.pending")}</span>}
                          <button className="pp-btn sec" onClick={() => { setNotice(null); setEditing(w.uuid); }}>{t("portal.review")}</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {shown.length === 0 && <p className="pp-sub">{t("portal.noneFound")}</p>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
